// Survey data export. Wraps the existing per-property and cross-store
// flatteners in src/lib/siteAttributeExportUtils.ts with a uniform API that
// the ExportsPanel UI can call.
//
// Survey exports are synchronous and run entirely in the browser — the data
// is already small enough that we don't need an edge function.

import { supabase } from "@/shared/integrations/supabase/client";
import { selectWithSurveySource } from "@/shared/integrations/supabase/surveySource";
import { fetchClientProperties } from "@/features/projects/services/projectsApi";
import {
  exportPropertiesToExcel,
  exportPropertiesToCsv,
} from "@/features/exports/services/siteAttributeExportUtils";
import { resolveSurveyAttributes } from "@/features/site-survey-editor/services/resolveSurveyAttributes";
import { fillAnswerGapsFromProdData } from "@/features/site-survey-editor/services/fillAnswerGapsFromProdData";
import { chunk } from "./_helpers";
import type { ExportFormat } from "./types";

// property_attributes rows carry heavy survey JSON; keep the `.in(...)` batch
// small so portfolio-scale cross-store exports stay under transport limits.
const ATTRIBUTES_PROPERTY_BATCH = 40;

interface ExportSurveyInput {
  projectId: string;
  projectName: string;
  // If set, only export this single property. Otherwise export all properties
  // in the project (cross-store).
  propertyId?: string;
  format: ExportFormat;
}

interface PropertyRow {
  id: string;
  name: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
}

interface AttributeRow {
  property_id: string;
  data: unknown;
  captured_at: string;
  /** property_attributes.id — for fetching live reviewer evidence. */
  survey_id?: string;
  /** Raw prod_data — carries answers for questions the tree doesn't have. */
  prod_data?: Record<string, unknown> | null;
}

export async function exportSurvey(input: ExportSurveyInput): Promise<void> {
  const properties = await fetchProperties(input.projectId, input.propertyId);
  if (properties.length === 0) {
    throw new Error("No properties found for export");
  }

  const propertyIds = properties.map((p) => p.id);
  const attributeRows = await fetchLatestAttributes(propertyIds);

  // The existing utilities accept loose `properties: any[]` and a typed
  // `PropertyAttributeRow[]`. Cast through here rather than refactor those.
  if (input.format === "xlsx") {
    exportPropertiesToExcel(attributeRows as never, properties, input.projectName);
  } else {
    exportPropertiesToCsv(attributeRows as never, properties, input.projectName);
  }
}

async function fetchProperties(
  projectId: string,
  propertyId?: string,
): Promise<PropertyRow[]> {
  return fetchClientProperties<PropertyRow>(
    projectId,
    "id, name, address, city, state",
    (q) => (propertyId ? q.eq("id", propertyId) : q),
  );
}

// Pulls the most recent property_attributes row per property. property_attributes
// can have multiple captures per property; we want the latest.
// Exported for the site-survey editor's templated bulk Excel export.
export async function fetchLatestAttributes(
  propertyIds: string[],
): Promise<AttributeRow[]> {
  if (propertyIds.length === 0) return [];

  // Chunk the `.in(...)` so a cross-store export over the whole portfolio doesn't
  // build an oversized request/response that tears down the connection. Each
  // property stays wholly within one chunk, preserving latest-per-property below.
  const selectClause = await selectWithSurveySource("id, property_id, data, prod_data, captured_at");
  type Raw = { id: string; property_id: string; data: unknown; prod_data?: unknown; survey_source?: string | null; captured_at: string };
  const latest = new Map<string, Raw>();

  for (const idChunk of chunk(propertyIds, ATTRIBUTES_PROPERTY_BATCH)) {
    const { data, error } = await supabase
      .from("property_attributes")
      .select(selectClause)
      .in("property_id", idChunk)
      .order("captured_at", { ascending: false });

    if (error) throw error;

    for (const row of (data ?? []) as unknown as Raw[]) {
      if (!latest.has(row.property_id)) latest.set(row.property_id, row);
    }
  }

  // Resolve each through the gate (self-describing prod_data when flagged,
  // else data), then fill remaining answer gaps from prod_data — exports ship
  // the best available answer even when the flagged tree is incomplete.
  const out: AttributeRow[] = [];
  for (const [propId, row] of latest) {
    const dataTree = typeof row.data === "string" ? JSON.parse(row.data) : (row.data ?? []);
    const prodData = (row.prod_data as Record<string, unknown> | null) ?? null;
    const { fields } = resolveSurveyAttributes({
      surveySource: row.survey_source,
      prodData,
      data: dataTree,
    });
    out.push({
      property_id: propId,
      survey_id: row.id,
      data: fillAnswerGapsFromProdData(fields, prodData),
      captured_at: row.captured_at,
      prod_data: prodData,
    });
  }
  return out;
}
